let shieldRegenStyleInjected = false;

const SEGMENT_COUNT = 5;

function ensureShieldRegenStyle(): void {
  if (shieldRegenStyleInjected || typeof document === 'undefined') return;
  shieldRegenStyleInjected = true;
  const style = document.createElement('style');
  style.textContent = [
    '@keyframes fpsShieldShimmer{',
    '0%,100%{filter:brightness(1);box-shadow:inset 0 0 4px rgba(46,230,255,0.15)}',
    '50%{filter:brightness(1.28);box-shadow:inset 0 0 10px rgba(126,249,255,0.5)}',
    '}',
    '.fps-shield-regen{border-color:var(--fps-accent)!important;box-shadow:0 0 8px rgba(46,230,255,0.25)}',
    '.fps-shield-regen .fps-shield-seg-fill{animation:fpsShieldShimmer 1.4s ease-in-out infinite}',
    '.fps-vitals-hud{display:flex;flex-direction:column;gap:5px}',
    '.fps-vitals-row{display:flex;gap:3px;align-items:stretch}',
    '.fps-vitals-seg{flex:1;height:10px;border-radius:var(--fps-radius-sm,4px);background:rgba(0,0,0,0.42);border:1px solid rgba(255,255,255,0.08);overflow:hidden;position:relative}',
    '.fps-vitals-seg-fill{position:absolute;inset:0;width:0%;transition:width 0.12s ease-out}',
    '.fps-health-seg-fill{background:linear-gradient(180deg,#ff8a55 0%,var(--fps-health,#ff6b35) 55%,#c94a20 100%);box-shadow:inset 0 1px 0 rgba(255,255,255,0.18)}',
    '.fps-shield-seg-fill{background:linear-gradient(180deg,#5ef0ff 0%,var(--fps-shield,#2ee6ff) 50%,#0d4a7a 100%);box-shadow:inset 0 1px 0 rgba(255,255,255,0.15)}',
    '.fps-vitals-label{font:700 9px var(--fps-font,system-ui,sans-serif);letter-spacing:0.14em;text-transform:uppercase;opacity:0.72;margin-bottom:1px}',
    '.fps-vitals-label-health{color:var(--fps-health,#ff6b35)}',
    '.fps-vitals-label-shield{color:var(--fps-shield,#2ee6ff)}',
  ].join('');
  document.head.appendChild(style);
}

/** Fortnite-style segmented health + shield bars (top-left). */
export class FpsShieldHud {
  private readonly root: HTMLDivElement;
  private readonly healthSegFills: HTMLDivElement[] = [];
  private readonly shieldSegFills: HTMLDivElement[] = [];
  private shield = 0;
  private health = 100;
  private regenActive = false;
  private disposed = false;

  constructor(mount: HTMLElement) {
    ensureShieldRegenStyle();
    this.root = document.createElement('div');
    this.root.className = 'fps-vitals-hud';
    this.root.style.cssText = [
      'position:fixed',
      'top:calc(env(safe-area-inset-top, 0px) + 14px)',
      'left:calc(env(safe-area-inset-left, 0px) + 14px)',
      'width:180px',
      'z-index:39',
      'pointer-events:none',
      'box-sizing:border-box',
      'padding:8px 10px',
      'border-radius:var(--fps-radius,10px)',
      'background:var(--fps-panel-bg-soft,rgba(10,14,24,0.72))',
      'backdrop-filter:blur(12px)',
      '-webkit-backdrop-filter:blur(12px)',
      'border:1px solid var(--fps-accent-dim,rgba(46,230,255,0.38))',
      'box-shadow:var(--fps-shadow,0 4px 20px rgba(0,0,0,0.28)),inset 0 1px 0 rgba(255,255,255,0.06)',
      'font-family:var(--fps-font,system-ui,sans-serif)',
    ].join(';');

    const healthLabel = document.createElement('div');
    healthLabel.className = 'fps-vitals-label fps-vitals-label-health';
    healthLabel.textContent = 'Health';
    this.root.appendChild(healthLabel);

    const healthRow = this.buildSegmentRow(this.healthSegFills, 'fps-health-seg-fill');
    this.root.appendChild(healthRow);

    const shieldLabel = document.createElement('div');
    shieldLabel.className = 'fps-vitals-label fps-vitals-label-shield';
    shieldLabel.textContent = 'Shield';
    shieldLabel.style.marginTop = '2px';
    this.root.appendChild(shieldLabel);

    const shieldRow = this.buildSegmentRow(this.shieldSegFills, 'fps-shield-seg-fill');
    this.root.appendChild(shieldRow);

    mount.appendChild(this.root);
    this.render();
  }

  private buildSegmentRow(fills: HTMLDivElement[], fillClass: string): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'fps-vitals-row';
    for (let i = 0; i < SEGMENT_COUNT; i++) {
      const seg = document.createElement('div');
      seg.className = 'fps-vitals-seg';
      const fill = document.createElement('div');
      fill.className = `fps-vitals-seg-fill ${fillClass}`;
      seg.appendChild(fill);
      row.appendChild(seg);
      fills.push(fill);
    }
    return row;
  }

  setShield(n: number): void {
    if (this.disposed || !Number.isFinite(n)) return;
    this.shield = Math.max(0, Math.min(100, n));
    this.render();
  }

  setHealth(n: number): void {
    if (this.disposed || !Number.isFinite(n)) return;
    this.health = Math.max(0, Math.min(100, n));
    this.render();
  }

  getShield(): number {
    return this.shield;
  }

  getHealth(): number {
    return this.health;
  }

  setRegenActive(active: boolean): void {
    if (this.disposed || this.regenActive === active) return;
    this.regenActive = active;
    this.root.classList.toggle('fps-shield-regen', active);
  }

  private renderSegmentRow(fills: HTMLDivElement[], value: number): void {
    const perSeg = 100 / SEGMENT_COUNT;
    for (let i = 0; i < SEGMENT_COUNT; i++) {
      const fill = fills[i];
      if (!fill) continue;
      const segStart = i * perSeg;
      const segEnd = segStart + perSeg;
      let pct = 0;
      if (value >= segEnd) pct = 100;
      else if (value > segStart) pct = ((value - segStart) / perSeg) * 100;
      fill.style.width = `${pct}%`;
    }
  }

  private render(): void {
    this.renderSegmentRow(this.healthSegFills, this.health);
    this.renderSegmentRow(this.shieldSegFills, this.shield);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.root.remove();
  }
}
